import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Eraser, Loader2, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEARN_MODES = [
  { value: "campus", label: "Im Dickies-Campus bearbeitet" },
  { value: "selbst", label: "Selbstständig erarbeitet" },
  { value: "anders", label: "Anderweitig erworben (z. B. Berufspraxis)" },
];

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1B1A18";
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInk.current = true;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
    onChange(null);
  };

  return (
    <div>
      <div className="rounded-2xl border-2 border-dashed border-cream-300 bg-white overflow-hidden relative">
        <canvas
          ref={canvasRef}
          width={700}
          height={220}
          data-testid="signature-canvas"
          className="w-full touch-none cursor-crosshair block"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <span className="absolute left-4 bottom-3 text-[11px] text-sand-400 pointer-events-none select-none inline-flex items-center gap-1">
          <PenLine className="h-3 w-3" /> Hier mit Finger oder Maus unterschreiben
        </span>
      </div>
      <button type="button" onClick={clear} data-testid="signature-clear-btn"
        className="mt-2 inline-flex items-center gap-1 text-xs text-sand-600 hover:text-terra">
        <Eraser className="h-3.5 w-3.5" /> Löschen & neu zeichnen
      </button>
    </div>
  );
}

export default function SignaturePage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | signed | done | invalid
  const [doc, setDoc] = useState(null);
  const [signature, setSignature] = useState(null);
  const [signedName, setSignedName] = useState("");
  const [learnMode, setLearnMode] = useState("campus");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/signatures/${token}`);
      setDoc(r.data);
      setSignedName(r.data.name || "");
      setState(r.data.status === "signed" ? "signed" : "ready");
    } catch {
      setState("invalid");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!signature) { setError("Bitte unterschreibe zuerst im Feld."); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/signatures/${token}/sign`, {
        signature_data_url: signature,
        signed_name: signedName,
        learn_mode: learnMode,
      });
      setState("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Senden fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 py-10 px-4" data-testid="signature-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <img src="/dh_logo_dark.webp" alt="Dickies-Campus" className="h-12 mx-auto mb-3" />
          <div className="text-[10.5px] uppercase tracking-[0.3em] font-bold text-terra">Dickies-Campus · Akademie für Pflege</div>
        </div>

        {state === "loading" && (
          <div className="text-center py-20 text-sand-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        )}

        {state === "invalid" && (
          <div className="rounded-3xl border border-cream-300 bg-white p-8 text-center" data-testid="signature-invalid">
            <h1 className="font-display text-2xl text-sand-800 mb-2">Link ungültig</h1>
            <p className="text-sand-600 text-sm">Dieser Unterschriften-Link ist ungültig oder abgelaufen. Bitte wende dich an dein Campus-Team: 0211 29273330</p>
          </div>
        )}

        {state === "signed" && (
          <div className="rounded-3xl border border-cream-300 bg-white p-8 text-center" data-testid="signature-already-signed">
            <CheckCircle2 className="h-10 w-10 text-sage-dark mx-auto mb-3" />
            <h1 className="font-display text-2xl text-sand-800 mb-2">Bereits unterschrieben</h1>
            <p className="text-sand-600 text-sm">Deine Bestätigung für <strong>{doc?.course_title}</strong> liegt uns bereits vor. Vielen Dank!</p>
          </div>
        )}

        {state === "done" && (
          <div className="rounded-3xl border border-cream-300 bg-white p-8 text-center" data-testid="signature-success">
            <CheckCircle2 className="h-10 w-10 text-sage-dark mx-auto mb-3" />
            <h1 className="font-display text-2xl text-sand-800 mb-2">Vielen Dank!</h1>
            <p className="text-sand-600 text-sm">
              Deine Teilnahmebestätigung für <strong>{doc?.course_title}</strong> wurde gespeichert
              und ist jetzt in deiner Teilnehmerakte hinterlegt.
            </p>
          </div>
        )}

        {state === "ready" && doc && (
          <div className="rounded-3xl border border-cream-300 bg-white p-7 sm:p-9 shadow-soft">
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra mb-2">Teilnahme- & Lernbestätigung</div>
            <h1 className="font-display text-3xl text-sand-800 tracking-tight mb-1">{doc.course_title}</h1>
            <p className="text-sm text-sand-600 mb-6">für {doc.name} · {doc.email}</p>

            {doc.modules?.length > 0 && (
              <div className="rounded-2xl bg-cream-100 border border-cream-300 p-5 mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-sand-600 mb-3">Kapitel & Module</div>
                <ul className="space-y-1.5">
                  {doc.modules.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-sand-700">
                      <CheckCircle2 className="h-4 w-4 text-terra shrink-0 mt-0.5" /> {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-sm text-sand-700 leading-relaxed mb-6 italic">
              „Hiermit bestätige ich, die oben aufgeführten Kapitel und Module vollständig bearbeitet
              bzw. mir die Inhalte selbstständig oder anderweitig angeeignet zu haben."
            </p>

            <label className="block mb-5">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sand-500 mb-1.5 block">Wie hast du die Inhalte erarbeitet?</span>
              <select value={learnMode} onChange={(e) => setLearnMode(e.target.value)}
                data-testid="signature-learn-mode"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra">
                {LEARN_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>

            <label className="block mb-5">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sand-500 mb-1.5 block">Vor- und Nachname</span>
              <input value={signedName} onChange={(e) => setSignedName(e.target.value)}
                data-testid="signature-name-input"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra" />
            </label>

            <div className="mb-6">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sand-500 mb-1.5 block">Deine Unterschrift</span>
              <SignaturePad onChange={setSignature} />
            </div>

            {error && <p className="text-sm text-red-600 mb-4" data-testid="signature-error">{error}</p>}

            <button type="button" onClick={submit} disabled={busy || signedName.trim().length < 2}
              data-testid="signature-submit-btn"
              className="w-full rounded-full bg-terra hover:bg-terra-dark disabled:opacity-50 text-white py-3.5 font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
              Verbindlich unterschreiben & absenden
            </button>
            <p className="text-[11px] text-sand-500 mt-3 text-center">
              Mit dem Absenden wird deine Unterschrift mit Zeitstempel gespeichert und als PDF in deiner Teilnehmerakte hinterlegt.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
